import type { i18n as I18n, TFunction } from 'i18next';

import { CIPageStatus } from '../viewcomp/general/ci_pagestatus';

import { daysSinceToday } from './datetime';

const MAX_MOBILE_SYNC_REMINDER_DAYS = 120;
export const PATIENT_CHART_BANNER_VIEWED_PREFIX = 'patientChartBannerViewed:';

export function getPatientChartBannerViewedStorageKey(patientId: string): string {
    return `${PATIENT_CHART_BANNER_VIEWED_PREFIX}${patientId}`;
}

function constructMessageForMobileSyncBanner(
    daysSinceLastMobileSync: number | null | undefined,
    t: TFunction,
    translationKey: string,
    defaultValue?: string
): string | null {
    if (!daysSinceLastMobileSync || daysSinceLastMobileSync < 1) {
        return null;
    }

    return t(translationKey, {
        ...(defaultValue ? { defaultValue } : {}),
        numberOfDays: Math.min(daysSinceLastMobileSync, MAX_MOBILE_SYNC_REMINDER_DAYS),
    });
}

export function checkIfPatientLanguageSupportedForSyncBanner(preferredLanguage: string, i18nInstance: I18n): boolean {
    const patientLanguageIsSupportedForSyncBanner = i18nInstance.exists('landing.patient_view_mobile_sync_needed', {
        lng: preferredLanguage,
        ns: 'patientApp',
        fallbackLng: [],
        fallbackNS: [],
    });

    return patientLanguageIsSupportedForSyncBanner;
}

export function checkIfUserLanguageSupportedForSyncBanner(preferredLanguage: string, i18nInstance: I18n): boolean {
    return i18nInstance.exists('landing.mobile_sync_needed_exact_days', {
        lng: preferredLanguage,
        ns: 'patientchart',
        fallbackLng: [],
        fallbackNS: [],
    });
}

export const constructMessageForPatientMobileSyncBanner = (
    daysSinceLastMobileSync: number | null | undefined,
    t: TFunction
): string | null =>
    constructMessageForMobileSyncBanner(daysSinceLastMobileSync, t, 'landing.patient_view_mobile_sync_needed');

export const constructMessageForPatientChartMobileSyncBanner = (
    daysSinceLastMobileSync: number | null | undefined,
    t: TFunction
): string | null =>
    constructMessageForMobileSyncBanner(daysSinceLastMobileSync, t, 'landing.mobile_sync_needed_exact_days');

export const handleMobileSyncNotification = (
    lastMobileSyncDate: string,
    preferredLanguage: string,
    i18nInstance: I18n,
    t: TFunction
): void => {
    if (!lastMobileSyncDate) {
        return;
    }
    const patientLangIsSupported = checkIfPatientLanguageSupportedForSyncBanner(preferredLanguage, i18nInstance);
    const daysSinceLastMobileSync = daysSinceToday(lastMobileSyncDate);
    const message = constructMessageForPatientMobileSyncBanner(daysSinceLastMobileSync, t);
    const hasPatientViewedBanner = sessionStorage.getItem('patientBannerViewed');

    // Combined early return
    if (!patientLangIsSupported || !message || (hasPatientViewedBanner && hasPatientViewedBanner !== 'false')) {
        return;
    }
    sessionStorage.setItem('patientBannerViewed', 'false');
    CIPageStatus.info(message, undefined, () => {
        sessionStorage.setItem('patientBannerViewed', 'true');
    });
};
